import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Form } from '@unform/web';
import { Box, Grid, Typography, Link as MUILink } from '@material-ui/core';

import imgBannerF from '../../assets/img/banner-f.png';
import imgIndiqueGanhe from '../../assets/img/main/indique-ganhe.png';
import imgCartaoVirtualMastercard from '../../assets/img/indique-ganhe/parceiros/logo-cartao-virtual-mastercard.png'
import imgIFood from '../../assets/img/indique-ganhe/parceiros/logo-ifood.png';

import useStyles from './useStyles';
import Container from '../../components/Container';
import NotEmpty from '../../components/NotEmpty';
import Button from '../../components/Form/Button';
import CampanhasPublicitarias from '../../components/CampanhasPublicitarias';
import Input from '../../components/Form/Input';
import TextHelper from '../../helpers/TextHelper';
import UtilHelper from '../../helpers/UtilHelper';
import ValidateHelper from '../../helpers/ValidateHelper';
import useAuth from '../../hooks/useAuth';
import useModal from '../../hooks/useModal';
import useOffline from '../../hooks/useOffline';
import useSnackbar from '../../hooks/useSnackbar';
import IndiqueGanheService from '../../services/IndiqueGanheService';
import PortalEgressoService from '../../services/PortalEgressoService';
import UsuarioService from '../../services/UsuarioService';
import AuthService from '../../services/AuthService';

const imgParceiros = {
  'logo-cartao-virtual-mastercard.png': imgCartaoVirtualMastercard,
  'logo-ifood.png': imgIFood,
};

const totalShowDefault = 9;

export default function Main() {
  const formContatoRef = useRef(null);

  const classes = useStyles();
  const modal = useModal();
  const snackbar = useSnackbar();
  const { user, context } = useAuth();

  const [totalShow, setTotalShow] = useState(totalShowDefault);
  const [loadingContato, setLoadingContato] = useState(false);

  const { id_matricula } = user;

  const getCursosGratuitos = useCallback(async () => {
    const { data } = await PortalEgressoService.getCursosGratuitos();
    const cursos = data?.dados || [];

    // Ordernar pelo nome do curso (melhorar UX)
    cursos.sort((a, b) => a.st_tituloexibicao.localeCompare(b.st_tituloexibicao));

    return { data: cursos };
  }, []);

  const handlePortalAluno = useCallback(async () => {
    const url = await AuthService.getUrlLoginDiretoPortalAluno(context, user);
    window.open(url, 'uny-portal-aluno');
  }, [context, user]);

  const getPerfil = useCallback(() => UsuarioService.getDadosPessoais(user.id_usuario, user.id_entidade), [user]);
  const getParceiros = useCallback(() => IndiqueGanheService.getParceiros(id_matricula), [id_matricula]);
  const getOportunidades = useCallback(() => PortalEgressoService.getOportunidades(user.id_entidade), [user]);
  const getBeneficios = useCallback(() => PortalEgressoService.getBeneficios(user.id_entidade), [user]);

  const { data: cursos } = useOffline('CursosGratuitos', getCursosGratuitos);
  const { data: perfil } = useOffline('Perfil', getPerfil);
  const { data: parceiros } = useOffline('IndiqueGanhe.Parceiros', getParceiros);
  const { data: oportunidades } = useOffline('Oportunidades', getOportunidades);
  const { data: beneficios } = useOffline('Beneficios', getBeneficios);

  useEffect(() => {
    if (perfil) {
      const { st_nomecompleto, st_email, nu_ddd, nu_telefone } = perfil;

      const nameSplit = st_nomecompleto.split(' ');
      const firstName = nameSplit.shift() || '';
      const lastName = nameSplit.pop() || '';

      const telefone = TextHelper.unmask(`${nu_ddd || ''}${nu_telefone || ''}`);

      formContatoRef.current.setData({
        nome: TextHelper.capitalize(`${firstName} ${lastName}`).trim(),
        email: st_email,
        telefone,
      });
    }
  }, [perfil]);

  const validateContato = (data) => {
    const errors = {};

    if (!`${data.nome}`.trim()) {
      errors.nome = 'Informe o nome corretamente';
    }

    if (!ValidateHelper.email(data.email)) {
      errors.email = 'Informe o e-mail corretamente';
    }

    if (!ValidateHelper.phone(data.telefone)) {
      errors.telefone = `Informe o DDD + telefone.`;
    }

    if (!`${data.mensagem}`.trim()) {
      errors.mensagem = 'Descreva o motivo do seu contato';
    }

    return errors;
  };

  const handleSubmitContato = async (dados) => {
    // Validação
    const errors = validateContato(dados);
    formContatoRef.current.setErrors(errors);

    if (Object.values(errors).length) {
      return modal.alert({
        title: 'Verifique os campos',
        content: ValidateHelper.listErrors(errors),
      });
    }

    setLoadingContato(true);

    try {
      const { data } = await PortalEgressoService.postFaleConosco(dados);

      modal.alert({
        title: data.title,
        content: data.text,
      });

      formContatoRef.current.clearField('mensagem');
    } catch (err) {
      snackbar.error(UtilHelper.getReponseMessage(err));
    }

    setLoadingContato(false);
  };

  return (
    <Container>
      <Box mb={8}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={8}>
            <Box align="center">
              <h2>
                Este é um espaço dedicado exclusivamente aos egressos dos cursos de{' '}
                <Box component="span" color="primary.contrastText" bgcolor="primary.dark" px={1}>
                  graduação e pós-graduação
                </Box>{' '}
                da Unyleya.
              </h2>
              <p className="text-justify-center">Que bom que estamos juntos novamente, seja bem-vindo ao nosso Portal do Egresso!</p>
              <p className="text-justify-center">
                Aqui queremos seguir presentes em sua jornada de qualificação e, para isso, preparamos materiais exclusivos sobre o mercado de trabalho, dicas
                para elaboração do seu CV, orientações de como participar de entrevistas de emprego, oferta de vagas de trabalho em nossos parceiros e
                convênios, além claro, de cursos de capacitação GRATUITOS para que você não perca o ritmo e siga se preparando para os desafios que ainda estão
                por vir.
              </p>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box my={8} align="center" id="centro-de-carreiras">
        <Box p={4} bgcolor="action.hover" borderRadius="10px">
          <Grid container alignItems="center" spacing={4}>
            <Grid item xs={12} md={6}>
              <img className="responsive" src={imgBannerF} alt="" />
            </Grid>
            <Grid item xs={12} md={6}>
              <h2 className={classes.subtitle}>
                centro de
                <Box component="span" color="primary.dark">
                  {' '}
                  carreiras
                </Box>
              </h2>
              <p className="text-justify-center">
                Ciente de que o cumprimento da sua missão institucional vai além do momento da conclusão dos cursos de graduação, a Unyleya apoia a inserção de
                seus discentes e egressos no mundo do trabalho. Em parceria com a Spot Seleções, ofertamos um leque de oportunidades profissionais para nossos
                alunos e graduados.
              </p>
              <Box align="center">
                <Button href="https://unyleya.edu.br/centro-carreiras" target="_blank">
                  acessar centro de carreiras
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box my={8} align="center" id="cursos-gratuitos">
        <h2 className={classes.subtitle}>
          cursos{' '}
          <Box component="span" color="primary.dark">
            gratuitos
          </Box>
        </h2>
        <NotEmpty data={cursos} placeholder="Buscando cursos..." empty="Nenhum curso disponível.">
          <Grid container justifyContent="center" alignItems="center">
            <Grid item xs={12} md={8}>
              <Box component="p" mb={4}>
                O egresso Unyleya tem acesso a cursos gratuitos para enriquecer o seu currículo. Escolha o curso de seu interesse logo abaixo e inicie agora
                mesmo.
              </Box>
            </Grid>
          </Grid>
          <Grid container alignItems="stretch" spacing={4}>
            {cursos?.slice(0, totalShow)?.map((curso) => (
              <Grid item key={curso.id_produto} xs={12} sm={6} md={4} className={classes.cursoItem}>
                <Box className={classes.cursoCard}>
                  <Typography className={classes.cursoTitle} variant="body1">
                    <Box component="span" fontWeight="500">
                      {TextHelper.capitalize(curso.st_tituloexibicao)}
                    </Box>
                  </Typography>
                  <Button className={clsx(classes.cursoBtn, 'custom-corners')} size="small" color="secondary" href={curso.urlPaginaCurso} target="_blank">
                    saiba mais
                  </Button>
                </Box>
              </Grid>
            ))}
          </Grid>
          {totalShow < cursos?.length && (
            <Box align="center" mt={6}>
              <Button color="secondary" variant="text" onClick={() => setTotalShow(totalShow + totalShowDefault)}>
                <b>ver mais cursos</b>
              </Button>
            </Box>
          )}
        </NotEmpty>
      </Box>

      {beneficios?.length > 0 && (
        <Box my={8} align="center" id="beneficios">
          <Grid container justifyContent="center" alignItems="center">
            <Grid item xs={12} md={8}>
              <h2 className={classes.subtitle}>
                <Box component="span" color="primary.dark">
                  benefícios
                </Box>{' '}
                e vantagens
              </h2>
              <p className="text-justify-center">
                Quando aluno, você conheceu os diferenciais e benefícios exclusivos de estudar na Unyleya. Agora nossas vantagens não se limitam aos alunos
                ativos, mas também a você, que já terminou o seu curso. No Portal do Egresso, você terá acesso a uma série de promoções, oportunidades e
                benefícios, veja algumas que já estão disponíveis para você.
              </p>
            </Grid>
          </Grid>
          <Box align="left" pt={3}>
            <CampanhasPublicitarias campanhas={beneficios} />
          </Box>
        </Box>
      )}

      {oportunidades?.length > 0 && (
        <Box my={8} align="center" id="oportunidades">
          <Grid container justifyContent="center" alignItems="center">
            <Grid item xs={12} md={8}>
              <h2 className={classes.subtitle}>
                <Box component="span" color="primary.dark">
                  oportunidades
                </Box>{' '}
                e promoções
              </h2>
              <p className="text-justify-center">
                A aprendizagem é um processo contínuo que não acaba com a graduação ou na primeira pós-graduação. Aproveite as vantagens de ser egresso Unyleya
                e dê continuidade no seu desenvolvimento acadêmico e profissional. Veja as oportunidades que preparamos para você!
              </p>
            </Grid>
          </Grid>
          <Box align="left" pt={3}>
            <CampanhasPublicitarias campanhas={oportunidades} />
          </Box>
        </Box>
      )}

      <Box my={8} align="center" id="indique-e-ganhe">
        <Box p={4} bgcolor="action.hover" borderRadius="10px">
          <Grid container justifyContent="center">
            <Grid item xs={12} md={8}>
              <p>
                <img className="responsive" src={imgIndiqueGanhe} alt="" />
              </p>
              <h2 className={classes.subtitle}>
                programa{' '}
                <Box component="span" color="primary.dark">
                  indique e ganhe
                </Box>
              </h2>
              <p className="text-justify-center">
                O aluno Unyleya já possui um programa de indicações exclusivo que é um sucesso. Agora, chegou a vez do egresso Unyleya também ganhar a sua
                versão do programa Indique e Ganhe.
              </p>
              <p className="text-justify-center">
                No{' '}
                <Box component="span" color="primary.dark">
                  Programa Indique e Ganhe
                </Box>{' '}
                você pode indicar os seus amigos, familiares, colegas de trabalho, dentre outros para estudarem conosco e em troca receber bonificações
                incríveis em vales-presente digitais Mastercard para compras online.
              </p>
              <NotEmpty data={parceiros}>
                <Box my={3}>
                  <Grid container justifyContent="center" alignItems="center" spacing={3}>
                    {parceiros?.map((parceiro) => (
                      <Grid key={parceiro.id_afiliadoindicacaoparceiro} item xs={6} md={3} lg={2}>
                        <img className="responsive" src={imgParceiros[parceiro.st_icone]} alt={parceiro.st_afiliadoindicacaoparceiro} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </NotEmpty>
              <p className="text-justify-center">
                E o melhor, sem limite de indicações.
                <br />
                Indicou, matriculou, ganhou!
              </p>
              <Grid container justifyContent="center" alignItems="center" spacing={2}>
                <Grid item>
                  <Button component={Link} to="/indique-e-ganhe/como-funciona" variant="outlined">
                    conhecer o programa
                  </Button>
                </Grid>
                <Grid item>
                  <Button component={Link} to="/indique-e-ganhe/indicar-pessoas">
                    indicar alguém
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box my={8} id="fale-conosco">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box px={4}>
              <Box component="h2" className={classes.subtitle} fontSize={40} mt={0}>
                fale <Box color="primary.dark">conosco</Box>
              </Box>
              <Box className="text-justify" mb={2}>
                Este é o canal de comunicação exclusivo para os egressos dos cursos da Unyleya.
              </Box>
              <Box>
                <Typography variant="caption">
                  <b>IMPORTANTE!</b>
                </Typography>{' '}
                <Typography variant="caption">
                  Caso você queira nos contatar a respeito de alguma questão ou solicitação referente ao seu curso – atual ou concluído, tais como documentação,
                  certificação, vista de prova, dentre outros, por favor, acesse o{' '}
                  <MUILink href="/#" onClick={handlePortalAluno}>
                    Portal do Aluno
                  </MUILink>{' '}
                  e abra uma ocorrência.
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box px={4}>
              <Form ref={formContatoRef} onSubmit={handleSubmitContato}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Input label="Nome" name="nome" size="small" autoComplete="name" placeholder="Digite seu nome" />
                  </Grid>
                  <Grid item xs={12}>
                    <Input type="email" label="E-mail" name="email" size="small" autoComplete="email" placeholder="Digite seu e-mail" />
                  </Grid>
                  <Grid item xs={12}>
                    <Input label="Telefone" name="telefone" size="small" autoComplete="tel-national" mask="phone" placeholder="Digite seu telefone" />
                  </Grid>
                  <Grid item xs={12}>
                    <Input label="Mensagem" name="mensagem" size="small" multiline rows={5} placeholder="Digite sua mensagem" />
                  </Grid>
                  <Grid item xs={12}>
                    <Box align="center">
                      <Button type="submit" loading={loadingContato}>
                        enviar mensagem
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
